/**
 * 将图片文件压缩到目标大小以内，返回 base64 DataURL 和可用于上传的 Blob。
 * HEIC/HEIF 在不支持的浏览器中会跳过 Canvas 压缩，直接上传原文件。
 */
export function compressImage(file: File, maxMB = 2): Promise<{ dataUrl: string; blob: Blob }> {
  return new Promise((resolve, reject) => {
    // HEIC/HEIF 格式：先读为 data URL，尝试 Canvas 解码，失败则直接透传原文件
    const isHeic = file.type === 'image/heic' || file.type === 'image/heif'
      || file.name.toLowerCase().endsWith('.heic')
      || file.name.toLowerCase().endsWith('.heif')

    const reader = new FileReader()
    reader.onload = () => {
      const img = new Image()
      img.onload = () => {
        const MAX = 1920
        let { width, height } = img
        if (width > MAX || height > MAX) {
          if (width > height) {
            height = Math.round(height * MAX / width)
            width = MAX
          } else {
            width = Math.round(width * MAX / height)
            height = MAX
          }
        }

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')!
        ctx.drawImage(img, 0, 0, width, height)

        let quality = 0.8
        const maxBytes = maxMB * 1024 * 1024
        const tryCompress = (): { dataUrl: string; blob: Blob } => {
          const dataUrl = canvas.toDataURL('image/jpeg', quality)
          const approxBytes = dataUrl.length * 0.75
          if (approxBytes > maxBytes && quality > 0.2) {
            quality -= 0.15
            return tryCompress()
          }
          const parts = dataUrl.split(',')
          const mime = parts[0].match(/:(.*?);/)![1]
          const bytes = atob(parts[1])
          const buf = new ArrayBuffer(bytes.length)
          const view = new Uint8Array(buf)
          for (let i = 0; i < bytes.length; i++) {
            view[i] = bytes.charCodeAt(i)
          }
          return { dataUrl, blob: new Blob([buf], { type: mime }) }
        }
        resolve(tryCompress())
      }
      img.onerror = () => {
        // HEIC/HEIF 在 Chrome/Firefox 中无法解码 → 直接透传原文件
        if (isHeic) {
          const parts = (reader.result as string).split(',')
          const mime = parts[0]?.match(/:(.*?);/)?.[1] || file.type || 'image/heic'
          const bytes = atob(parts[1] || '')
          const buf = new ArrayBuffer(bytes.length)
          const view = new Uint8Array(buf)
          for (let i = 0; i < bytes.length; i++) {
            view[i] = bytes.charCodeAt(i)
          }
          resolve({
            dataUrl: reader.result as string,
            blob: new Blob([buf], { type: mime }),
          })
        } else {
          reject(new Error('图片加载失败'))
        }
      }
      img.src = reader.result as string
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * 压缩图片并上传到服务器，返回服务器 URL。
 * HEIC 文件上传时保留原扩展名。
 */
export async function uploadImage(
  file: File,
  uploadFn: (formData: FormData) => Promise<{ url: string }>,
  maxMB = 2
): Promise<{ url: string; dataUrl: string }> {
  const { dataUrl, blob } = await compressImage(file, maxMB)
  const formData = new FormData()
  // HEIC 文件保留原后缀，其它格式统一用 .jpg
  const fileName = file.name.toLowerCase().endsWith('.heic') ? 'image.heic'
    : file.name.toLowerCase().endsWith('.heif') ? 'image.heif'
    : 'image.jpg'
  formData.append('file', blob, fileName)
  const result = await uploadFn(formData)
  return { url: result.url, dataUrl }
}
